import React, {ReactNode} from 'react';
import {useField} from 'formik';

import {institutionTypes, InstitutionType} from 'shared/constants';
import Label from 'shared/components/label';
import Loading from 'shared/components/loading';
import FormikDropdown from 'src/components/formik-dropdown';
import AccessibleTypeahead, {TypeaheadItem} from 'shared/components/accessible-typeahead';
import {schoolQueryResults} from 'shared/actions/high-school-typeaheads';
import {collegeQueryResults} from 'shared/actions/college-typeaheads';
import {EducationPhase} from 'src/types/enums';

export interface Props {
  renderLabel?: (params: InstitutionType) => ReactNode; // return '' for no label
  institutionType: 'high-school' | 'community-college' | 'dual-enrollment';
  typeaheadProps?: Record<string, $TSFixMe>;
  fieldName?: string;
  location?: [lat: number, long: number];
  noTopPadding?: boolean;
  onSelect?: (result: $TSFixMe) => void;
  showCampus?: boolean;
}

const InstitutionTypeahead = ({
  typeaheadProps,
  noTopPadding,
  institutionType,
  renderLabel = ({singular}) => singular,
  location,
  fieldName = 'currentInstitution',
  onSelect,
  showCampus,
}: Props) => {
  const [field, meta, helpers] = useField(fieldName);
  const institutionTypeObj = institutionTypes[institutionType];

  const handleBlur = () => {
    helpers.setTouched(true);

    if (!field.value.parentId) {
      helpers.setValue({
        ...field.value,
        parentId: '',
        name: '',
      });
    }
  };

  const renderLoading = () => (
    <div className="institution-typeahead-searching">
      <Loading />

      <p className="institution-typeahead-header">{`Searching ${institutionTypeObj.plural}...`}</p>
      <p className="institution-typeahead-body">This may take a minute</p>
    </div>
  );

  const renderEmptyResult = () => (
    <div>
      <span className="institution-typeahead-header">
        {`Can't find your ${institutionTypeObj.singular.toLowerCase()}?`}
      </span>
      <span className="institution-typeahead-body">&nbsp;Check out the search tips below.</span>
    </div>
  );

  const handleSearch = (query: string) =>
    institutionTypeObj.type === EducationPhase.HIGH_SCHOOL
      ? schoolQueryResults(query, [], location)
      : collegeQueryResults(query, [], false, location);

  const handleInputChange = (value: string) => {
    helpers.setValue({
      ...field.value,
      parentId: '',
      name: value,
    });
  };

  const handleSelect = (result) => {
    const isCollege = institutionTypeObj.type === EducationPhase.COMMUNITY_COLLEGE;
    const isHs = institutionTypeObj.type === EducationPhase.HIGH_SCHOOL;
    const isDualEnrollment = institutionTypeObj.type === 'dual-enrollment';

    helpers.setValue({
      ...field.value,
      _id: result._id,
      parentId: isHs ? result._id : isCollege ? result.parentId : result.directoryInfoId,
      name: result.text,
      type: isDualEnrollment ? 'CollegeCourseProvider' : result.educationPhase,
      ...(isCollege && {campuses: result.campuses, campus: null}),
    });

    onSelect?.(result);
  };

  const typeahead = (
    <AccessibleTypeahead
      id="find-institution-typeahead"
      value={
        !field.value.parentId
          ? null
          : {
              text: field.value.name,
              _id: field.value.parentId,
            }
      }
      placeholder={`e.g. ${institutionTypeObj.example}`}
      error={meta.touched && typeof meta.error === 'string' ? meta.error : ''}
      onSearch={handleSearch}
      onInputChange={handleInputChange}
      onSelect={handleSelect}
      onBlur={handleBlur}
      renderMenuItemChildren={(option) => <TypeaheadItem {...option} />}
      // @ts-expect-error
      showCheck={meta.touched && !!meta.error?.parentId}
      renderLoading={renderLoading}
      renderEmptyResult={renderEmptyResult}
      cy="institution-typeahead"
      {...typeaheadProps}
    />
  );

  const label = renderLabel(institutionTypeObj);

  return (
    <div className={`${institutionTypeObj.type}-institution-typeahead`}>
      {label ? (
        <Label required noTopPadding={noTopPadding}>
          <span>{label}</span>
          {typeahead}
        </Label>
      ) : (
        typeahead
      )}

      {showCampus && !!field.value.campuses?.length && (
        <Label required>
          Where do you take most of your classes?
          <FormikDropdown
            name={`${fieldName}.campus`}
            options={field.value.campuses.map((value) => ({
              text: value.name,
              value: value._id,
            }))}
            required
          />
        </Label>
      )}
    </div>
  );
};

export default InstitutionTypeahead;
